import { z } from "zod";
import { useEffect } from "react";
import { Button, Input } from "antd";
import { toast } from "react-toastify";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import Head from "../../../../Components/Head";
import useRHForm from "../../../../Components/form/RHForm";
import { ErrorLabel } from "../../../../Components/atomic";
import ControlledField from "../../../../Components/form/ControlledField";
import styleWebUrlCreate from "./WebUrlCreate.module.css";
import {
  useCreateWebUrl,
  useGetWebUrlDetail,
  useUpdateWebUrl,
} from "../../../../api/webUrls";

const WebUrlCreate = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { data: webUrl } = useGetWebUrlDetail(id);
  const {
    mutateAsync: mutateAsyncCreate,
    error: errorCreate,
    isLoading: isLoadingCreate,
  } = useCreateWebUrl();
  const {
    mutateAsync: mutateAsyncUpdate,
    error: errorUpdate,
    isLoading: isLoadingUpdate,
  } = useUpdateWebUrl(id);

  const { name, url } = webUrl ?? state?.item ?? {};

  const {
    Form,
    methods: {
      control,
      setValue,
      formState: { errors },
    },
  } = useRHForm({
    initialValues: id ? { name, url } : {},
    schema: z.object({
      name: z
        .string({ required_error: "Name is required." })
        .min(1, "Name is required."),
      url: z
        .string({ required_error: "URL is required." })
        .min(1, "URL is required.")
        .url("URL must be valid and include http:// or https://")
        .refine(
          value => value.startsWith("http://") || value.startsWith("https://"),
          "URL must include http:// or https://"
        ),
    }),
  });

  useEffect(() => {
    if (webUrl) {
      setValue("name", webUrl.name, { shouldValidate: true });
      setValue("url", webUrl.url, { shouldValidate: true });
    }
  }, [webUrl, setValue]);

  const handleSubmit = data => {
    const payload = { name: data.name, url: data.url };

    if (id) {
      mutateAsyncUpdate(payload)
        .then(res => {
          if (res?.data) {
            toast.success(res?.data?.message || "Web Url Updated");
            navigate("/web-twinprocms/web-urls");
          }
        })
        .catch(error => {
          toast.error(
            error?.response?.data?.message ||
              error?.message ||
              "Failed to update Web URL."
          );
        });
      return;
    }

    mutateAsyncCreate(payload)
      .then(res => {
        if (res?.data) {
          toast.success(res?.data?.message || "Web Url Created");
          navigate("/web-twinprocms/web-urls");
        }
      })
      .catch(error => {
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to create Web URL."
        );
      });
  };

  const isLoading = isLoadingCreate || isLoadingUpdate;
  const serverError = errorCreate || errorUpdate;

  return (
    <main>
      <Head title="Web URL" />
      <div className={styleWebUrlCreate["title-container"]}>
        <h1>{id ? "Edit Web URL" : "Add Web URL"}</h1>
      </div>
      <section className={styleWebUrlCreate["form-wrapper"]}>
        <Form onSubmit={handleSubmit}>
          <ControlledField
            required
            name="name"
            label="Name"
            control={control}
            Component={Input}
            errors={errors}
          />
          <ErrorLabel>{errors?.name?.message}</ErrorLabel>
          <ErrorLabel>{serverError?.response?.data?.name?.toString()}</ErrorLabel>

          <ControlledField
            required
            name="url"
            label="URL"
            control={control}
            Component={Input}
            errors={errors}
          />
          <ErrorLabel>{errors?.url?.message}</ErrorLabel>
          <ErrorLabel>{serverError?.response?.data?.url?.toString()}</ErrorLabel>

          <div className={styleWebUrlCreate["footer-btn-container"]}>
            <Button
              htmlType="submit"
              loading={isLoading}
              disabled={isLoading}
              className={styleWebUrlCreate["submit-btn"]}
            >
              {!isLoading && <FontAwesomeIcon icon={faCheck} />}
              Submit
            </Button>
            <Button
              className={styleWebUrlCreate["cancel-btn"]}
              onClick={() => navigate("/web-twinprocms/web-urls")}
            >
              Cancel
            </Button>
          </div>
        </Form>
      </section>
    </main>
  );
};

export default WebUrlCreate;
